import React, { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, Check } from "lucide-react";

/**
 * Lightweight square avatar cropper.
 * Props:
 *  - file: File object
 *  - onCancel: () => void
 *  - onConfirm: (Blob) => Promise<void>
 */
function calculateCropSourceRect(imgEl, zoom, offset, SIZE) {
  const drawnW = imgEl.naturalWidth * zoom;
  const drawnH = imgEl.naturalHeight * zoom;
  const ix = (SIZE - drawnW) / 2 + offset.x;
  const iy = (SIZE - drawnH) / 2 + offset.y;
  return {
    sx: Math.max(0, (-ix) / zoom),
    sy: Math.max(0, (-iy) / zoom),
    sw: SIZE / zoom,
    sh: SIZE / zoom,
  };
}

export default function AvatarCropModal({ file, onCancel, onConfirm }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgEl, setImgEl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const startRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const stageRef = useRef(null);
  const SIZE = 280;

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onImgLoad = (e) => {
    const img = e.currentTarget;
    setImgEl(img);
    // Initial fit: cover the square
    const fit = Math.max(SIZE / img.naturalWidth, SIZE / img.naturalHeight);
    setZoom(fit);
    setOffset({ x: 0, y: 0 });
  };

  const startDrag = (e) => {
    const point = "touches" in e ? e.touches[0] : e;
    setDragging(true);
    startRef.current = { x: point.clientX, y: point.clientY, ox: offset.x, oy: offset.y };
  };
  const onMove = (e) => {
    if (!dragging) return;
    const point = "touches" in e ? e.touches[0] : e;
    const dx = point.clientX - startRef.current.x;
    const dy = point.clientY - startRef.current.y;
    setOffset({ x: startRef.current.ox + dx, y: startRef.current.oy + dy });
  };
  const endDrag = () => setDragging(false);

  const confirm = async () => {
    if (!imgEl || submitting) return;
    setSubmitting(true);
    try {
      const canvas = document.createElement("canvas");
      const out = 512;
      canvas.width = out;
      canvas.height = out;
      const ctx = canvas.getContext("2d");
      const { sx, sy, sw, sh } = calculateCropSourceRect(imgEl, zoom, offset, SIZE);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, out, out);
      ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, out, out);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      await onConfirm(blob);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 fade-up">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[var(--c-border)] shadow-2xl p-6" data-testid="crop-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="section-title text-lg">قصّ الصورة</h3>
          <button onClick={onCancel} className="text-[var(--c-muted)] hover:text-[var(--c-navy)]" data-testid="crop-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-4">
          <div
            ref={stageRef}
            onMouseDown={startDrag} onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}
            onTouchStart={startDrag} onTouchMove={onMove} onTouchEnd={endDrag}
            className="relative bg-[var(--c-cream)] rounded-full overflow-hidden cursor-grab active:cursor-grabbing select-none"
            style={{ width: SIZE, height: SIZE, touchAction: "none" }}
          >
            {imgUrl && (
              <img
                src={imgUrl}
                onLoad={onImgLoad}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  left: "50%", top: "50%",
                  transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                  transformOrigin: "center",
                  maxWidth: "none",
                  pointerEvents: "none",
                }}
              />
            )}
            <div className="absolute inset-0 ring-2 ring-[var(--c-gold)]/70 rounded-full pointer-events-none" />
          </div>

          <div className="w-full flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-[var(--c-muted)]" />
            <input
              type="range" min="0.2" max="4" step="0.01"
              value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[var(--c-gold)]" data-testid="crop-zoom"
            />
            <ZoomIn className="w-4 h-4 text-[var(--c-muted)]" />
          </div>

          <div className="flex w-full gap-3 pt-2">
            <button onClick={onCancel} className="flex-1 rounded-xl py-3 border border-[var(--c-border)] text-[var(--c-navy)] hover:border-[var(--c-gold)]/50 transition" data-testid="crop-cancel">
              إلغاء
            </button>
            <button onClick={confirm} disabled={submitting} className="btn-navy flex-1 rounded-xl py-3 inline-flex items-center justify-center gap-2 font-bold" data-testid="crop-confirm">
              <Check className="w-4 h-4" />
              <span>{submitting ? "جارٍ…" : "تأكيد"}</span>
            </button>
          </div>

          <p className="text-[11px] text-[var(--c-muted)] text-center">اسحب الصورة بالماوس واستخدم الشريط للتكبير.</p>
        </div>
      </div>
    </div>
  );
}
